import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";

import { listMarketOfferBucketCounts, listMarketOfferBucketCountsReset } from "../../actions";
import { type OfferBucket, type OfferBucketCount } from "./types";

export type OfferBucketCounts = Partial<Record<OfferBucket, number>>;

interface RootState {
  listMarketOfferBucketCounts: {
    isFetching: boolean;
    errorMessage: string;
    data: OfferBucketCount[] | { results: [] };
  };
}

/** Country-wide offers per bucket (`status_counts?group_by=bucket`), for the offer tabs. */
export const useOfferBucketCounts = () => {
  const dispatch = useDispatch<Dispatch<UnknownAction>>();
  const { isFetching, errorMessage, data } = useSelector(
    (state: RootState) => state.listMarketOfferBucketCounts
  );

  const refresh = useCallback(() => {
    dispatch(listMarketOfferBucketCounts() as unknown as UnknownAction);
  }, [dispatch]);

  useEffect(() => {
    refresh();

    return () => {
      dispatch(listMarketOfferBucketCountsReset() as unknown as UnknownAction);
    };
  }, [dispatch, refresh]);

  const counts = useMemo<OfferBucketCounts>(
    () =>
      (Array.isArray(data) ? data : []).reduce<OfferBucketCounts>(
        (sums, { bucket, count }) => ({ ...sums, [bucket]: count }),
        {}
      ),
    [data]
  );

  return {
    counts,
    refresh,
    isLoading: isFetching || (!Array.isArray(data) && !errorMessage),
  };
};
